import { useQueryClient } from "react-query";
import { useEffect, useRef } from "react";
import {
  PreviewQuerySchemaBackgroundJobResultQuery,
  Scalars,
  StartPreviewQuerySchemaBackgroundJobQuery,
  usePreviewQuerySchemaBackgroundJobResultQuery,
  useStartPreviewQuerySchemaBackgroundJobQuery,
} from "src/graphql";
import { QueryType } from "src/types/models";
import { ModelResultData } from "src/utils/models";
import { backoffPollIntervalMs } from "src/utils/poll-backoff";

type Props = {
  sourceId?: string;
  queryType?: string | null;
  getQuery?: () => Scalars["JSONObject"];
  onStart: () => { runId: string };
  onComplete: ({
    runId,
    data,
  }: {
    runId: string;
    data?: ModelResultData;
  }) => void;
  onError: (error: Error) => void;
};

// Roughly preserves the previous ~5 minute timeout: with the backoff
// schedule capped at 5s per poll, 60 polls is ~4.7 minutes total wall
// time (was 600 polls * 500ms = 5 min before).
const MAX_POLLS = 60;

export const usePreviewQuerySchema = ({
  sourceId,
  queryType,
  getQuery,
  onStart,
  onComplete,
  onError,
}: Props) => {
  const client = useQueryClient();
  const pollTimeoutRef = useRef<NodeJS.Timeout>();

  // Stop polling on unmount
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const getSchema = async (
    queryOverride?: Scalars["JSONObject"],
    queryTypeOverride?: QueryType,
  ) => {
    const query = queryOverride ?? getQuery?.();
    const effectiveQueryType = queryTypeOverride ?? queryType;

    if (!(sourceId && effectiveQueryType && query)) {
      return {};
    }

    const { runId } = onStart();
    let error: Error | undefined = undefined;
    let data: ModelResultData | undefined;

    try {
      data = await getSchemaAsync({
        sourceId,
        queryType: effectiveQueryType,
        query,
      });
    } catch (e) {
      error = e;
      onError(e);
    }

    onComplete({ data, runId });
    return { data, error };
  };

  // It would be nice to update the polling mechanism to use react-query, but the useModelRun
  // hook depends on this function returning the schema synchronously, and that hook has many
  // downstream dependencies. We should eventually refactor that hook API to decouple kicking
  // off the PreviewQuerySchema and waiting for the results
  const getSchemaAsync = async (variables: {
    sourceId: string;
    queryType: string;
    query: Scalars["JSONObject"];
  }) => {
    const startResponse =
      await client.fetchQuery<StartPreviewQuerySchemaBackgroundJobQuery>(
        useStartPreviewQuerySchemaBackgroundJobQuery.getKey(variables),
        {
          queryFn:
            useStartPreviewQuerySchemaBackgroundJobQuery.fetcher(variables),
          cacheTime: 0,
        },
      );

    const jobId = startResponse.startPreviewQuerySchemaBackgroundJob.jobId;

    return new Promise<ModelResultData>((resolve, reject) => {
      let pollCount = 0;

      const pollForResults = async () => {
        pollCount++;

        if (pollCount > MAX_POLLS) {
          reject(new Error("Operation timed out"));
          return;
        }

        try {
          const resultResponse =
            await client.fetchQuery<PreviewQuerySchemaBackgroundJobResultQuery>(
              usePreviewQuerySchemaBackgroundJobResultQuery.getKey({ jobId }),
              {
                queryFn: usePreviewQuerySchemaBackgroundJobResultQuery.fetcher({
                  jobId,
                }),
                cacheTime: 0,
              },
            );

          const result = resultResponse.previewQuerySchemaBackgroundJobResult;

          if (result.__typename === "PreviewQuerySchemaBackgroundJobSuccess") {
            resolve({
              rows: undefined,
              columns: result.result.columns,
              exceedsPreviewMax: false,
              usedSampledModels: false,
              sampledModelsRate: 0.0,
              inferredPrimaryKey: null,
              roleResolution: null,
            });
          } else if (
            result.__typename === "PreviewQuerySchemaBackgroundJobFailure"
          ) {
            reject(new Error(result.error));
          } else if (
            result.__typename === "PreviewQuerySchemaBackgroundJobProcessing"
          ) {
            // Continue polling with exponential backoff. pollCount is the
            // number of polls already issued; pollCount - 1 is the attempt
            // index for the next interval (0-based).
            pollTimeoutRef.current = setTimeout(
              pollForResults,
              backoffPollIntervalMs(pollCount - 1),
            );
          }
        } catch (error) {
          reject(error);
        }
      };

      void pollForResults();
    });
  };

  return { getSchema };
};
